import type { Suggestion } from '~/components/SearchSuggest/types';
import type { LocationProviders } from '~~/core/ports/repositories/LocationAutocomplete';
import type { Ref, ShallowRef } from 'vue';

import { SEARCH_SUGGEST } from '~/constants';

interface UseSuggestCoreOptions {
	searchTerm: Ref<string>;
	providers?: LocationProviders[];
	hasSelectedValue?: Ref<boolean>;
	isDropdownOpen?: Ref<boolean>;
}

/**
 * Thin orchestration layer over useLocationAutocomplete
 * Provides computed helpers for code validation and search state
 *
 * @param options - Search term and provider configuration
 * @returns Core search state and helpers
 */
export function useSuggestCore(options: UseSuggestCoreOptions) {
	const { searchTerm, providers, hasSelectedValue, isDropdownOpen } = options;
	const { selectedMacroregion } = useTwoFieldSearchSuggestState();

	// Location autocomplete - now directly consumes macroregion state internally
	const {
		data: locationAutocompleteData,
		loading: apiLoading,
		execute: executeLocationAutocomplete
	} = useLocationAutocomplete(searchTerm, {
		providers,
		hasSelectedValue: hasSelectedValue as ShallowRef<boolean> | undefined,
		isDropdownOpen,
		macroregion: selectedMacroregion
	});

	// Watch selectedMacroregion to re-execute when macroregion changes
	watch(selectedMacroregion, () => {
		if (
			searchTerm.value.length >= SEARCH_SUGGEST.MIN_SEARCH_LENGTH &&
			!hasSelectedValue?.value &&
			(isDropdownOpen?.value ?? true)
		) {
			executeLocationAutocomplete();
		}
	});

	// Minimum search term length
	const hasMinSearchTerm = computed<boolean>(
		() => searchTerm.value.length >= 3
	);

	// Code validation
	const shouldShowCodeGroup = computed<boolean>(() => {
		const regex = /^[a-zA-Z0-9]{2,5}(\d{1,8})?$/m;
		const isValidCode = regex.test(searchTerm.value);
		return isValidCode && hasMinSearchTerm.value;
	});

	// Search by code option
	const searchByCodeOption = computed<Suggestion[]>(() => {
		const value = searchTerm.value.toUpperCase();

		if (
			!hasMinSearchTerm.value ||
			!shouldShowCodeGroup.value ||
			apiLoading.value
		) {
			return [];
		}

		return [
			{
				result: {
					title: value,
					subtitle: ''
				},
				id: crypto.randomUUID(),
				searchParams: {
					code: value,
					regions: [],
					cities: [],
					macroregions: [],
					placeId: null,
					condoSlug: null
				},
				searchType: 'code'
			}
		];
	});

	return {
		// State
		hasMinSearchTerm,
		locationAutocompleteData,
		loading: apiLoading,
		shouldShowCodeGroup,
		searchByCodeOption,

		// Actions
		executeLocationAutocomplete
	};
}
